'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import SubscriptionCard from './SubscriptionCard';
import SavingsCalculator from './SavingsCalculator';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface Benefit {
  id: string;
  text: string;
  icon: string;
}

interface CategoryDiscount {
  category: string;
  range: string;
  icon: string;
}

interface ComparisonFeature {
  feature: string;
  featureEn: string;
  vip: boolean | string;
  family: boolean | string;
}

const SubscriptionCardsInteractive = () => {
  const router = useRouter();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const { t } = useLanguage();

  const vipBenefits: Benefit[] = [
    { id: '1', text: t('خصومات حصرية تصل إلى 40% على الملابس والأزياء', 'Exclusive discounts up to 40% on clothing and fashion'), icon: 'SparklesIcon' },
    { id: '2', text: t('خصومات طبية تصل إلى 30% في أفضل المراكز الصحية', 'Medical discounts up to 30% at top health centers'), icon: 'HeartIcon' },
    { id: '3', text: t('خصومات على المطاعم والمقاهي تصل إلى 25%', 'Restaurant and cafe discounts up to 25%'), icon: 'BuildingStorefrontIcon' },
    { id: '4', text: t('خصومات رياضية تصل إلى 35% في النوادي الرياضية', 'Sports discounts up to 35% at sports clubs'), icon: 'TrophyIcon' },
    { id: '5', text: t('خصومات على الحلويات والمخبوزات تصل إلى 30%', 'Sweets and bakery discounts up to 30%'), icon: 'CakeIcon' },
    { id: '6', text: t('أولوية في الحجوزات والمواعيد', 'Priority in bookings and appointments'), icon: 'ClockIcon' },
    { id: '7', text: t('عروض حصرية شهرية لحاملي البطاقة', 'Monthly exclusive offers for cardholders'), icon: 'GiftIcon' },
    { id: '8', text: t('دعم فني متاح على مدار الساعة', '24/7 technical support available'), icon: 'ChatBubbleLeftRightIcon' },
  ];

  const familyBenefits: Benefit[] = [
    { id: '1', text: t('خصومات على الملابس تصل إلى 25%', 'Clothing discounts up to 25%'), icon: 'ShoppingBagIcon' },
    { id: '2', text: t('خصومات طبية تصل إلى 20% للعائلة', 'Medical discounts up to 20% for the family'), icon: 'HeartIcon' },
    { id: '3', text: t('خصومات على المطاعم تصل إلى 15%', 'Restaurant discounts up to 15%'), icon: 'BuildingStorefrontIcon' },
    { id: '4', text: t('خصومات رياضية تصل إلى 25%', 'Sports discounts up to 25%'), icon: 'TrophyIcon' },
    { id: '5', text: t('خصومات على الحلويات تصل إلى 20%', 'Sweets discounts up to 20%'), icon: 'CakeIcon' },
    { id: '6', text: t('مناسبة لجميع أفراد العائلة', 'Suitable for all family members'), icon: 'UsersIcon' },
    { id: '7', text: t('عروض عائلية خاصة', 'Special family offers'), icon: 'HomeIcon' },
  ];

  const vipDiscounts: CategoryDiscount[] = [
    { category: t('الطب والصحة', 'Medical & Health'), range: '10% - 30%', icon: 'HeartIcon' },
    { category: t('المطاعم والمقاهي', 'Restaurants & Cafes'), range: '10% - 25%', icon: 'BuildingStorefrontIcon' },
    { category: t('الملابس والأزياء', 'Clothing & Fashion'), range: '15% - 40%', icon: 'ShoppingBagIcon' },
    { category: t('الحلويات والمخبوزات', 'Sweets & Bakeries'), range: '10% - 30%', icon: 'CakeIcon' },
    { category: t('الرياضة واللياقة', 'Sports & Fitness'), range: '15% - 35%', icon: 'TrophyIcon' },
  ];

  const familyDiscounts: CategoryDiscount[] = [
    { category: t('الطب والصحة', 'Medical & Health'), range: '10% - 20%', icon: 'HeartIcon' },
    { category: t('المطاعم والمقاهي', 'Restaurants & Cafes'), range: '10% - 15%', icon: 'BuildingStorefrontIcon' },
    { category: t('الملابس والأزياء', 'Clothing & Fashion'), range: '15% - 25%', icon: 'ShoppingBagIcon' },
    { category: t('الحلويات والمخبوزات', 'Sweets & Bakeries'), range: '10% - 20%', icon: 'CakeIcon' },
    { category: t('الرياضة واللياقة', 'Sports & Fitness'), range: '15% - 25%', icon: 'TrophyIcon' },
  ];

  const comparisonFeatures: ComparisonFeature[] = [
    { feature: 'عدد المستفيدين', featureEn: 'Number of Beneficiaries', vip: t('6 أشخاص', '6 People'), family: t('6 أشخاص', '6 People') },
    { feature: 'مدة الصلاحية', featureEn: 'Validity Period', vip: t('سنة واحدة', 'One Year'), family: t('سنة واحدة', 'One Year') },
    { feature: 'خصومات الملابس', featureEn: 'Clothing Discounts', vip: t('حتى 40%', 'Up to 40%'), family: t('حتى 25%', 'Up to 25%') },
    { feature: 'خصومات الطب', featureEn: 'Medical Discounts', vip: t('حتى 30%', 'Up to 30%'), family: t('حتى 20%', 'Up to 20%') },
    { feature: 'خصومات المطاعم', featureEn: 'Restaurant Discounts', vip: t('حتى 25%', 'Up to 25%'), family: t('حتى 15%', 'Up to 15%') },
    { feature: 'خصومات الرياضة', featureEn: 'Sports Discounts', vip: t('حتى 35%', 'Up to 35%'), family: t('حتى 25%', 'Up to 25%') },
    { feature: 'خصومات الحلويات', featureEn: 'Sweets Discounts', vip: t('حتى 30%', 'Up to 30%'), family: t('حتى 20%', 'Up to 20%') },
    { feature: 'أولوية الحجوزات', featureEn: 'Booking Priority', vip: true, family: false },
    { feature: 'عروض حصرية شهرية', featureEn: 'Monthly Exclusive Offers', vip: true, family: false },
    { feature: 'دعم فني 24/7', featureEn: '24/7 Technical Support', vip: true, family: true },
  ];

  const handleSubscribe = (cardType: 'VIP' | 'Family') => {
    router.push(`/subscription-registration?type=${cardType}`);
  };

  const handleSubscribeFamily3Month = () => {
    router.push(`/subscription-registration?type=Family3Month`);
  };

  const handleSubscribePersonal = () => {
    router.push(`/subscription-registration?type=Personal`);
  };

  const handleSubscribeCorporate = () => {
    router.push(`/subscription-registration?type=Corporate`);
  };

  return (
    <div className="space-y-8">
      {/* Success Message */}
      {showSuccessMessage && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 bg-success text-success-foreground px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 animate-bounce">
          <Icon name="CheckCircleIcon" size={24} />
          <span className="font-medium">{t('جاري تحويلك لإتمام الاشتراك...', 'Redirecting to complete subscription...')}</span>
        </div>
      )}

      {/* Hero Section */}
      <div className="text-center mb-12">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary font-amiri mb-4">
          {t('اختر بطاقة الاشتراك المناسبة لك', 'Choose the Right Subscription Card for You')}
        </h1>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
          {t('استمتع بخصومات حصرية على مدار العام في أفضل الشركاء التجاريين', 'Enjoy exclusive discounts year-round at the best business partners')}
        </p>
      </div>

      {/* Unified Subscription Cards and Comparison Section */}
      <div className="bg-card rounded-xl shadow-lg border border-border p-6 md:p-8 mb-12">
        {/* Subscription Cards - VIP and Family */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <SubscriptionCard
            type="VIP"
            price={1500}
            capacity={6}
            cardImage="/assets/images/Copy_of_Black_and_Gold_Classy_Business_Card_1_-1769730611123.png"
            benefits={vipBenefits}
            discounts={vipDiscounts}
            onSubscribe={() => handleSubscribe('VIP')}
          />
          <SubscriptionCard
            type="Family"
            price={800}
            capacity={6}
            cardImage="https://cmgesbyqfxkmwckadqpy.supabase.co/storage/v1/object/sign/cards/Copy%20of%20Black%20and%20Gold%20Classy%20Business%20Card%20(2).png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8xNWJhNmJlNC00YzUzLTQwZTItOGI4OC1mYjg1OWNlNzM1YTQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJjYXJkcy9Db3B5IG9mIEJsYWNrIGFuZCBHb2xkIENsYXNzeSBCdXNpbmVzcyBDYXJkICgyKS5wbmciLCJpYXQiOjE3Njk3MjY4MzgsImV4cCI6MTgwMTI2MjgzOH0.XgpPwAgAvHL2ORitbHRazwhAMAB_TyVQCd9IiJ36-04"
            benefits={familyBenefits}
            discounts={familyDiscounts}
            isPopular={true}
            onSubscribe={() => handleSubscribe('Family')}
          />
        </div>
        
        {/* Personal Annual Card and Family 3-Month Card */}
        <div className="grid md:grid-cols-2 gap-8">
          <SubscriptionCard
            type="Personal"
            price={150}
            capacity={1}
            cardImage="/assets/images/WhatsApp_Image_2026-03-29_at_8.21.55_PM-1774805462574.jpeg"
            benefits={familyBenefits}
            discounts={familyDiscounts}
            duration="سنة كاملة"
            onSubscribe={handleSubscribePersonal}
          />
          <SubscriptionCard
            type="Family3Month"
            price={250}
            capacity={6}
            cardImage="https://ocadeynpyrbhhpnmlizj.supabase.co/storage/v1/object/sign/123/79f1af79-1181-47d8-aab5-ea80271c243b.jfif?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV82ZDJlYmU2My03ZDcwLTQ1ODYtOWNhMC1mMWM2NjNiZjZkZDUiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiIxMjMvNzlmMWFmNzktMTE4MS00N2Q4LWFhYjUtZWE4MDI3MWMyNDNiLmpmaWYiLCJpYXQiOjE3NzE0MjA2OTcsImV4cCI6MTgwMjk1NjY5N30.XcajdrtHIc02v8uoPEULefJE0tiU4djawI4RTIxfkoE"
            benefits={familyBenefits}
            discounts={familyDiscounts}
            duration="3 أشهر فقط"
            onSubscribe={handleSubscribeFamily3Month}
          />
        </div>

        {/* Corporate Card - Full width below */}
        <div className="mt-8">
          <SubscriptionCard
            type="Corporate"
            price={150}
            capacity={0}
            cardImage="https://ocadeynpyrbhhpnmlizj.supabase.co/storage/v1/object/sign/123/WhatsApp%20Image%202026-04-25%20at%2012.38.41%20PM.jpeg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV82ZDJlYmU2My03ZDcwLTQ1ODYtOWNhMC1mMWM2NjNiZjZkZDUiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiIxMjMvV2hhdHNBcHAgSW1hZ2UgMjAyNi0wNC0yNSBhdCAxMi4zOC40MSBQTS5qcGVnIiwiaWF0IjoxNzc3MTA5OTYxLCJleHAiOjE4MDg2NDU5NjF9.cWo1_g8SuflpVV_jnW7wLBIKZLOJV8_EGRhZyMvNx9E"
            benefits={familyBenefits}
            discounts={familyDiscounts}
            duration="سنة كاملة"
            onSubscribe={handleSubscribeCorporate}
          />
        </div>
      </div>

      {/* Savings Calculator */}
      <SavingsCalculator />

      {/* FAQ Section */}
      <div className="bg-card rounded-xl shadow-lg border border-border p-6 md:p-8">
        <h3 className="text-xl md:text-2xl font-bold text-primary font-amiri mb-6 flex items-center gap-3">
          <Icon name="QuestionMarkCircleIcon" size={28} />
          <span>{t('الأسئلة الشائعة', 'Frequently Asked Questions')}</span>
        </h3>
        <div className="space-y-4">
          <details className="group">
            <summary className="flex items-center justify-between cursor-pointer p-4 bg-muted rounded-lg hover:bg-muted/80 transition-premium">
              <span className="font-medium text-foreground">{t('كيف يمكنني تفعيل البطاقة؟', 'How can I activate the card?')}</span>
              <Icon name="ChevronDownIcon" size={20} className="group-open:rotate-180 transition-premium" />
            </summary>
            <div className="p-4 text-sm text-muted-foreground">
              {t(
                'بعد إتمام عملية الشراء، ستصلك البطاقة عبر البريد الإلكتروني ويمكنك تفعيلها مباشرة من لوحة التحكم الخاصة بك.',
                'After completing the purchase, the card will be sent to you via email and you can activate it directly from your dashboard.'
              )}
            </div>
          </details>

          <details className="group">
            <summary className="flex items-center justify-between cursor-pointer p-4 bg-muted rounded-lg hover:bg-muted/80 transition-premium">
              <span className="font-medium text-foreground">{t('هل يمكن استخدام البطاقة في جميع الفروع؟', 'Can the card be used at all branches?')}</span>
              <Icon name="ChevronDownIcon" size={20} className="group-open:rotate-180 transition-premium" />
            </summary>
            <div className="p-4 text-sm text-muted-foreground">
              {t(
                'نعم، يمكنك استخدام البطاقة في جميع فروع الشركاء المشاركين في البرنامج على مستوى المملكة.',
                'Yes, you can use the card at all branches of participating partners across the Kingdom.'
              )}
            </div>
          </details>

          <details className="group">
            <summary className="flex items-center justify-between cursor-pointer p-4 bg-muted rounded-lg hover:bg-muted/80 transition-premium">
              <span className="font-medium text-foreground">{t('ما هي مدة صلاحية البطاقة؟', 'What is the card validity period?')}</span>
              <Icon name="ChevronDownIcon" size={20} className="group-open:rotate-180 transition-premium" />
            </summary>
            <div className="p-4 text-sm text-muted-foreground">
              {t(
                'البطاقة صالحة لمدة سنة كاملة من تاريخ التفعيل، ويمكنك تجديدها بعد انتهاء المدة.',
                'The card is valid for a full year from the activation date, and you can renew it after the period ends.'
              )}
            </div>
          </details>

          <details className="group">
            <summary className="flex items-center justify-between cursor-pointer p-4 bg-muted rounded-lg hover:bg-muted/80 transition-premium">
              <span className="font-medium text-foreground">{t('هل يمكن استرجاع قيمة الاشتراك؟', 'Can the subscription value be refunded?')}</span>
              <Icon name="ChevronDownIcon" size={20} className="group-open:rotate-180 transition-premium" />
            </summary>
            <div className="p-4 text-sm text-muted-foreground">
              {t(
                'لا يمكن استرجاع قيمة الاشتراك بعد تفعيل البطاقة، ولكن يمكنك نقل البطاقة لشخص آخر قبل التفعيل.',
                'The subscription value cannot be refunded after card activation, but you can transfer the card to another person before activation.'
              )}
            </div>
          </details>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCardsInteractive;